import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { afterPageLoad } from "./utils/afterPageLoad";
import LanguageDetector from "i18next-browser-languagedetector";

/*
 * ONE LANGUAGE'S STRINGS TRAVEL WITH THE PAGE, NOT THREE (2026-10).
 *
 * The three locale files were imported here and bundled into the main
 * script: 872 KB of its 3.5 MB, escaped to ASCII by the minifier, most of
 * it for languages the visitor is not reading. Each file is now its own
 * chunk, fetched by i18next's backend hook for the language the page boots
 * in (index.js awaits that before the first render, as it always awaited
 * `changeLanguage`) and for a language switched to later. English is the
 * fallback language, so i18next also fetches it for a Hebrew or Arabic page —
 * a key a locale lacks still reads in English, as it did when all three were
 * bundled (the locale parity test is top-level only; nested gaps exist).
 *
 * Under Jest the files are required in place and handed over synchronously
 * (`initImmediate: false` makes i18next load them during `init`), so a test
 * that imports this module has every string at the first render, as before.
 * webpack evaluates the `NODE_ENV` test at build time and leaves the
 * requires out of the browser's bundle.
 */
const LOCALE_CHUNKS = {
  en: () => import(/* webpackChunkName: "locale-en" */ "./locales/en/tanslationEn.json"),
  he: () => import(/* webpackChunkName: "locale-he" */ "./locales/he/tanslationHe.json"),
  ar: () => import(/* webpackChunkName: "locale-ar" */ "./locales/ar/translationAr.json"),
};

function bundledForTests(language) {
  // The requires sit INSIDE the constant `if`: webpack skips a branch it can
  // see is never taken, and would bundle a require after an early return.
  if (process.env.NODE_ENV === "test") {
    if (language === "he") return require("./locales/he/tanslationHe.json");
    if (language === "ar") return require("./locales/ar/translationAr.json");
    if (language === "en") return require("./locales/en/tanslationEn.json");
  }
  return null;
}

/** i18next backend: a language's strings, from its chunk — or, under Jest, from the file. */
export const localeChunkBackend = {
  type: "backend",
  init() {},
  read(language, _namespace, callback) {
    const bundled = bundledForTests(language);
    if (bundled) return callback(null, bundled);
    const load = LOCALE_CHUNKS[language];
    if (!load) return callback(null, {});
    load().then(
      (mod) => callback(null, mod.default ?? mod),
      (err) => callback(err, null),
    );
  },
};

i18n
  .use(LanguageDetector)
  .use(localeChunkBackend)
  .use(initReactI18next)
  .init({
    /* THE FALLBACK LANGUAGE COMES WHEN THE LINE IS IDLE (2026-10-01). With `fallbackLng: "en"` at init, i18next
       loaded English beside Hebrew on every ad landing — a second 38 KB chunk down a 4G line before the buy button.
       Hebrew and Arabic carry every top-level key (the parity test); a nested gap reads its `defaultValue` for the
       few seconds until English arrives, after which the fallback is set and used as before. */
    fallbackLng: false,
    supportedLngs: ["en", "ar", "he"],
    load: "languageOnly",
    initImmediate: process.env.NODE_ENV !== "test",
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "language",
      caches: [], // don't auto-save detected language; only save when user picks in UI
    },
    interpolation: {
      escapeValue: false,
      // Money strings carry a {{currency}} placeholder. It defaults to ₪ so
      // Israel renders exactly as before even when a call site doesn't pass a
      // currency; non-ILS salons override it with currencySymbol(salonCurrency).
      defaultVariables: { currency: "₪" },
    },
  });

export default i18n;

/** English as the fallback, loaded once the line is idle (see `fallbackLng` above). Returns the promise for tests. */
export function loadFallbackLanguageWhenIdle(win = typeof window !== "undefined" ? window : undefined) {
  const start = () => i18n.loadLanguages("en").then(() => { i18n.options.fallbackLng = ["en"]; }).catch(() => {});
  if (!win) return start();
  // The same moment the pixel waits for: `load`, every picture in, nothing new asked for in a while — an idle
  // callback fires at once on a line that is merely waiting for the main script, which is not idle at all.
  return new Promise((resolve) => { afterPageLoad(() => resolve(start()), { ceilingMs: 12000 }); });
}
if (process.env.NODE_ENV !== "test") loadFallbackLanguageWhenIdle();
